<template>
  <AppMenuNew
    ref="menuRef"
    placement="bottom-start"
    :offset="8"
    @visibility-change="handleVisibilityChange"
  >
    <template #trigger="{ toggleMenu }">
      <button
        class="h-10 text-surface-800 bg-transparent hover:bg-surface-200 duration-150 rounded-md overflow-hidden"
        data-testid="nav-workspace-button"
        @click="toggleMenu"
      >
        <div
          v-if="
            workspaceStore.currentWorkspace &&
            userStore.user &&
            !workspaceStore.$isInitialising
          "
          class="h-full grid grid-cols-[2.5rem_auto_0.75rem] items-center gap-2 overflow-hidden pr-3"
        >
          <div class="flex justify-center items-center">
            <AppWorkspaceProfileImageSmall
              :workspace="workspaceStore.currentWorkspace"
            />
          </div>
          <span
            class="text-left text-sm font-medium truncate grow tracking-tight"
            >{{ workspaceStore.currentWorkspace.title }}</span
          >
          <AppIcon
            icon="fa-solid fa-chevron-down"
            class="text-2xs text-surface-600 shrink-0"
          />
        </div>

        <div
          v-else
          class="h-full w-full animate-pulse bg-surface-300"
        ></div>
      </button>
    </template>

    <template #items>
      <AppMenuNewItem
        icon="fa-regular fa-gear"
        label="Workspace settings"
        :command="goToWorkspaceSettings"
      />

      <AppMenuNewDivider />

      <!-- Opens on hover (TRV-1208). -->
      <AppMenuNewSubMenu
        icon="fa-regular fa-arrows-repeat"
        label="Switch workspace"
      >
        <template #items>
          <AppMenuNewItem
            v-for="workspace in workspaceOptions"
            :key="workspace.id"
            :label="workspace.title"
            :command="() => switchWorkspace(workspace.id)"
          />
        </template>
      </AppMenuNewSubMenu>

      <AppMenuNewItem
        v-if="currentOrganizationSlug && canOpenOrganizationSettings"
        icon="fa-regular fa-building"
        label="Organisation settings"
        :command="goToOrganizationSettings"
      />
      <AppMenuNewItem
        v-if="userStore.isSuperUser"
        icon="fa-regular fa-gear-complex-code"
        label="System Admin"
        :command="goToSystemAdmin"
      />

      <AppMenuNewItem
        icon="fa-regular fa-arrow-right-from-bracket"
        label="Sign out"
        danger
        :command="() => authStore.signOut()"
      />
    </template>
  </AppMenuNew>
</template>

<script setup lang="ts">
import { useAuthStore } from '~/stores/auth'
import { useUserStore } from '~/stores/user'
import { useUserWorkspacesStore } from '~/stores/userWorkspaces'
import { useWorkspaceStore } from '~/stores/workspace'
import { Permission } from '~/types/v2/Access'

const authStore = useAuthStore()
const userStore = useUserStore()
const workspaceStore = useWorkspaceStore()
const userWorkspacesStore = useUserWorkspacesStore()
const route = useRoute()
const router = useRouter()

// Selecting a workspace lives in the "Switch workspace" submenu, which only
// closes itself — so close the top-level menu explicitly on switch.
const menuRef = ref<{ closeMenu: () => void } | null>(null)

// Other workspaces the user can switch to (excludes the current one).
const workspaceOptions = computed(() => {
  if (
    userStore.$isInitialising ||
    !userStore.user ||
    workspaceStore.$isInitialising
  ) {
    return []
  }

  return userWorkspacesStore.userWorkspaces.filter(
    (workspace) => workspace.id !== workspaceStore.currentWorkspaceId,
  )
})

async function switchWorkspace(workspaceId: string) {
  const organizationSlug =
    userStore.getOrganizationSlugForWorkspace(workspaceId)
  if (!organizationSlug) {
    throw new Error(`No organization slug for workspace ${workspaceId}`)
  }
  menuRef.value?.closeMenu()
  await router.push({
    name: 'organizationSlug-workspaceId',
    params: { organizationSlug, workspaceId },
  })
}

// Org settings lands on /{org}/settings, which redirects to the Summary tab.
// Resolve the slug from the route when in an org-scoped area, otherwise from the
// current workspace.
const currentOrganizationSlug = computed(() => {
  if (
    typeof route.params.organizationSlug === 'string' &&
    route.params.organizationSlug
  ) {
    return route.params.organizationSlug
  }
  if (workspaceStore.currentWorkspaceId) {
    return userStore.getOrganizationSlugForWorkspace(
      workspaceStore.currentWorkspaceId,
    )
  }
  return null
})

// The item lands on Summary (/{org}/settings redirects there), so it shows to
// whoever can open Summary: Owners, Org admins and Superadmins.
const canOpenOrganizationSettings = useCan(
  Permission.OrganizationSettingsManage,
  () => ({
    organizationId: userStore.organizations.find(
      (organization) => organization.slug === currentOrganizationSlug.value,
    )?.id,
  }),
)

async function goToWorkspaceSettings() {
  await router.push({ name: 'organizationSlug-workspaceId-settings' })
}

async function goToOrganizationSettings() {
  if (!canOpenOrganizationSettings.value) return
  const organizationSlug = currentOrganizationSlug.value
  if (!organizationSlug) return
  await router.push({
    name: 'organizationSlug-settings',
    params: { organizationSlug },
  })
}

async function goToSystemAdmin() {
  if (!userStore.isSuperUser) return
  await router.push({ name: 'system-admin-summary' })
}

const forceNavOpen = inject<Ref<boolean>>('forceNavOpen', ref(false))

function handleVisibilityChange(visible: boolean) {
  forceNavOpen.value = visible
}
</script>
